/**
 * Двухпанельный режим широкого окна (десктоп и веб): слева постоянный список чатов, справа —
 * весь прежний стек экранов (чат, «Новое сообщение», статья канала и т. д.).
 *
 * <p>Почему список — отдельный экземпляр поверх стека, а чат остаётся в стеке. Экран чата
 * держится на навигаторе: шапку собирает через `setOptions`, звонок и переходы делает через
 * `navigate`/`push`/`goBack`, фокус берёт из `useIsFocused`. Вынести его из стека значило бы
 * переписать половину экрана. Списку же от навигации нужно немногое — `navigate` и
 * `useFocusEffect`, — и это даёт маленький переходник `sidebarNavigation` ниже. В стеке на
 * месте списка при этом стоит заглушка «Выберите чат».
 *
 * <p>Только веб: на планшете нативный стек со своими жестами назад — отдельная работа, а
 * телефоны сюда не попадают по ширине.
 */
import { createContext, useContext, useEffect, useState } from 'react';
import { Platform, useWindowDimensions } from 'react-native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { navigationRef } from './navigationRef';
import type { RootStackParamList } from './types';

/** Ширина левой панели: как у десктопных мессенджеров, строке чата хватает на имя и превью. */
export const CHAT_SIDEBAR_WIDTH = 380;
/** С этой ширины окна правой панели остаётся ≥ 520 px — меньше чат уже тесный. */
const WIDE_LAYOUT_MIN_WIDTH = 900;

export function isWideLayout(width: number, platform: string = Platform.OS): boolean {
  return platform === 'web' && width >= WIDE_LAYOUT_MIN_WIDTH;
}

/**
 * Ширина, которая достаётся экрану справа. Окно целиком — только без двух панелей: всё, что
 * меряет себя от окна (предел пузыря), иначе вылезало бы за правую панель.
 */
export function chatPaneWidth(windowWidth: number, platform: string = Platform.OS): number {
  return isWideLayout(windowWidth, platform) ? windowWidth - CHAT_SIDEBAR_WIDTH : windowWidth;
}

export function useWideLayout(): boolean {
  const { width } = useWindowDimensions();
  return isWideLayout(width);
}

type ListParams = RootStackParamList['ChatList'];

/** Что левой панели нужно знать о стеке: открытый справа чат и параметры маршрута списка. */
export type PaneSnapshot = { chatId: string | null; savedOpen: boolean; listParams: ListParams };

type RootState = ReturnType<typeof navigationRef.getRootState>;

export function paneSnapshotOf(state: RootState | undefined): PaneSnapshot {
  if (!state || !Array.isArray(state.routes)) return { chatId: null, savedOpen: false, listParams: undefined };
  const top = state.routes[state.index ?? state.routes.length - 1];
  const list = state.routes.find((r) => r.name === 'ChatList');
  const chat = top?.name === 'Chat' ? (top.params as RootStackParamList['Chat'] | undefined) : undefined;
  return {
    chatId: chat?.chatId ?? null,
    // «Избранное» стоит шапкой списка, а не строкой с id, — ему свой признак.
    savedOpen: Boolean(chat?.isSaved),
    listParams: list?.params as ListParams,
  };
}

/** Снимок стека для левой панели; обновляется на каждое изменение навигации. */
export function usePaneSnapshot(): PaneSnapshot {
  const read = () => paneSnapshotOf(navigationRef.isReady() ? navigationRef.getRootState() : undefined);
  const [snap, setSnap] = useState<PaneSnapshot>(read);
  useEffect(() => {
    const sync = () =>
      setSnap((prev) => {
        const next = read();
        return prev.chatId === next.chatId && prev.savedOpen === next.savedOpen && prev.listParams === next.listParams
          ? prev
          : next;
      });
    sync();
    return navigationRef.addListener('state', sync);
  }, []);
  return snap;
}

/**
 * Открыть экран в правой панели. Стек сбрасывается до «заглушка → экран»: каждый щелчок по
 * чату в списке даёт свежий экран чата. Обычный `navigate('Chat')` при уже открытом чате
 * подменил бы ему параметры, оставив состояние прошлой переписки (черновик, прокрутку).
 */
export function openInPane<N extends keyof RootStackParamList>(name: N, params?: RootStackParamList[N]): void {
  if (!navigationRef.isReady()) return;
  if (name === 'ChatList') {
    navigationRef.navigate('ChatList', params as ListParams);
    return;
  }
  const state = navigationRef.getRootState() as RootState | undefined;
  const routes = state?.routes ?? [];
  const top = state ? routes[state.index] : undefined;
  if (name === 'Chat' && top?.name === 'Chat') {
    const next = params as RootStackParamList['Chat'];
    const open = top.params as RootStackParamList['Chat'] | undefined;
    // Тот же чат ещё раз — ничего не делать, иначе щелчок по открытому чату терял бы черновик.
    if (open?.chatId === next.chatId && !next.focusMessageId) return;
  }
  const list = routes.find((r) => r.name === 'ChatList');
  navigationRef.reset({
    index: 1,
    routes: [
      list ? { name: 'ChatList', key: list.key, params: list.params } : { name: 'ChatList' },
      { name, params },
    ],
  });
}

/**
 * Навигация для списка в левой панели. Список вне стека, поэтому своего `navigation` у него
 * нет; ему нужны `navigate` (всё уходит в правую панель) и то, на чём стоит `useFocusEffect`:
 * панель видна всегда, значит, «в фокусе» всегда, а событий фокуса не бывает.
 */
const noop = () => {};
export const sidebarNavigation = {
  navigate: (name: keyof RootStackParamList, params?: object) =>
    openInPane(name, params as RootStackParamList[typeof name]),
  push: (name: keyof RootStackParamList, params?: object) =>
    openInPane(name, params as RootStackParamList[typeof name]),
  isFocused: () => true,
  addListener: () => noop,
  removeListener: noop,
  canGoBack: () => false,
  goBack: noop,
  setParams: noop,
  setOptions: noop,
  getParent: () => undefined,
  getId: () => undefined,
  getState: () => (navigationRef.isReady() ? navigationRef.getRootState() : undefined),
  dispatch: (action: Parameters<typeof navigationRef.dispatch>[0]) => navigationRef.dispatch(action),
} as unknown as NativeStackNavigationProp<RootStackParamList, 'ChatList'>;

/** Что открыто справа — чтобы список подсветил строку. Вне двух панелей — ничего. */
export type PaneSelection = { chatId: string | null; savedOpen: boolean };
const NO_SELECTION: PaneSelection = { chatId: null, savedOpen: false };
const PaneSelectionContext = createContext<PaneSelection>(NO_SELECTION);
export const PaneSelectionProvider = PaneSelectionContext.Provider;
export function usePaneSelection(): PaneSelection {
  return useContext(PaneSelectionContext);
}
